import { memo, useState, useCallback, useEffect } from "react";
import { useI18n } from "@/i18n";
import { useRole } from "@/hooks/useRole";
import { Pagination, usePagination } from "@/design-system";
import {
  listTypeUses,
  createTypeUse,
  updateTypeUse,
  deleteTypeUse,
  isTypeUseError,
} from "@/infrastructure/type-use/typeUseService";
import { formatDate } from "@/utils/formatDate";
import type { TypeUse, TypeUseErrorCode } from "@/domain/types";
import { colors, typography, transitions } from "@/design-system/tokens";
import { FormModal, DeleteModal } from "./components/TypeUseModals";

export const TypeUsePage = memo(function TypeUsePage() {
  const { t } = useI18n();
  const { canCreateTypeUse, canEditTypeUse } = useRole();
  const [items, setItems] = useState<TypeUse[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<TypeUse | null>(null);
  const [deletingItem, setDeletingItem] = useState<TypeUse | null>(null);
  const [page, setPage] = useState(1);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listTypeUses();
      setItems(data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const { paginatedItems, totalPages, currentPage, totalItems } = usePagination(items, page);

  const showToast = useCallback((message: string, type: "success" | "error") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const getErrorMessage = useCallback(
    (code: TypeUseErrorCode): string => {
      const map: Record<TypeUseErrorCode, string> = {
        DUPLICATE: t.typeUse.errorDuplicate,
        VALIDATION: t.typeUse.errorMinLength,
        NOT_FOUND: t.typeUse.errorNotFound,
        FORBIDDEN: t.typeUse.errorForbidden,
        UNAUTHORIZED: t.typeUse.errorForbidden,
        UNKNOWN: t.typeUse.errorForbidden,
      };
      return map[code];
    },
    [t.typeUse]
  );

  const handleCreate = useCallback(
    async (name: string) => {
      try {
        await createTypeUse(name);
        showToast(t.typeUse.successCreated, "success");
        setShowForm(false);
        fetchItems();
      } catch (err) {
        if (isTypeUseError(err)) showToast(getErrorMessage(err.message as TypeUseErrorCode), "error");
      }
    },
    [fetchItems, showToast, t.typeUse.successCreated, getErrorMessage]
  );

  const handleUpdate = useCallback(
    async (id: string, name: string) => {
      try {
        await updateTypeUse(id, name);
        showToast(t.typeUse.successUpdated, "success");
        setEditingItem(null);
        setShowForm(false);
        fetchItems();
      } catch (err) {
        if (isTypeUseError(err)) showToast(getErrorMessage(err.message as TypeUseErrorCode), "error");
      }
    },
    [fetchItems, showToast, t.typeUse.successUpdated, getErrorMessage]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await deleteTypeUse(id);
        showToast(t.typeUse.successDeleted, "success");
        setDeletingItem(null);
        fetchItems();
      } catch (err) {
        if (isTypeUseError(err)) showToast(getErrorMessage(err.message as TypeUseErrorCode), "error");
      }
    },
    [fetchItems, showToast, t.typeUse.successDeleted, getErrorMessage]
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "24px",
        padding: "36px 36px 48px",
        minHeight: "100vh",
        background: "#fff",
        boxSizing: "border-box",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <h1 style={{ fontFamily: typography.fontFamily, fontSize: "1.9rem", fontWeight: typography.weight.bold, color: "#111", margin: 0, letterSpacing: "-0.03em" }}>
            {t.typeUse.title}
          </h1>
          <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#999", margin: "4px 0 0" }}>
            {t.typeUse.subtitle}
          </p>
        </div>
        {canCreateTypeUse && <button
          onClick={() => { setShowForm(true); setEditingItem(null); }}
          style={{
            fontFamily: typography.fontFamily,
            fontSize: typography.size.sm,
            fontWeight: typography.weight.bold,
            color: "#fff",
            background: colors.primary,
            border: "none",
            borderRadius: "10px",
            padding: "10px 20px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            transition: `background ${transitions.fast}`,
          }}
          onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.background = colors.primaryHover)}
          onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.background = colors.primary)}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
          {t.typeUse.addTypeUse}
        </button>}
      </div>

      <div
        style={{
          background: "#fff",
          borderRadius: "20px",
          border: "1px solid #e8e8e8",
          padding: "28px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          minHeight: "300px",
        }}
      >
        {loading && <LoadingState />}
        {!loading && items.length === 0 && <EmptyState />}
        {!loading && items.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #f2f2f2" }}>
                  {[t.typeUse.nameLabel, t.typeUse.createdAt, canEditTypeUse ? t.users.colActions : ""].filter(Boolean).map(col => (
                    <th key={col} style={{ fontFamily: typography.fontFamily, fontSize: "10px", fontWeight: typography.weight.bold, color: "#bbb", textTransform: "uppercase", letterSpacing: "0.1em", padding: "14px 16px", textAlign: "left" }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedItems.map(item => (
                  <tr key={item.id} style={{ borderBottom: "1px solid #f7f7f7", transition: `background ${transitions.fast}` }} onMouseEnter={e => ((e.currentTarget as HTMLTableRowElement).style.background = "#fafafa")} onMouseLeave={e => ((e.currentTarget as HTMLTableRowElement).style.background = "transparent")}>
                    <td style={cellStyle}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#111" }}>{item.name}</span></td>
                    <td style={cellStyle}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#888" }}>{formatDate(item.created)}</span></td>
                    {canEditTypeUse && (
                      <td style={cellStyle}>
                        <div style={{ display: "flex", gap: "6px" }}>
                          <RowBtn label={t.typeUse.editTypeUse} onClick={() => { setEditingItem(item); setShowForm(true); }} />
                          <RowBtn label={t.typeUse.deleteTypeUse} onClick={() => setDeletingItem(item)} danger />
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div style={{ marginTop: "12px" }}>
                <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} onPageChange={setPage} />
              </div>
            )}
          </div>
        )}
      </div>

      {showForm && (
        <FormModal
          item={editingItem}
          onSave={(name) => editingItem ? handleUpdate(editingItem.id, name) : handleCreate(name)}
          onClose={() => { setShowForm(false); setEditingItem(null); }}
        />
      )}

      {deletingItem && (
        <DeleteModal
          item={deletingItem}
          onConfirm={() => handleDelete(deletingItem.id)}
          onClose={() => setDeletingItem(null)}
        />
      )}

      {toast && <Toast message={toast.message} type={toast.type} />}
    </div>
  );
});

function Toast({ message, type }: { message: string; type: "success" | "error" }) {
  return (
    <div
      role="alert"
      style={{
        position: "fixed",
        bottom: "24px",
        right: "24px",
        background: type === "success" ? "#111" : colors.error,
        color: "#fff",
        fontFamily: typography.fontFamily,
        fontSize: typography.size.sm,
        fontWeight: typography.weight.medium,
        padding: "12px 20px",
        borderRadius: "10px",
        boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
        zIndex: 300,
        animation: "fadeInUp 0.3s ease",
      }}
    >
      {message}
    </div>
  );
}

function EmptyState() {
  const { t } = useI18n();
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "64px 24px", gap: "12px", textAlign: "center" }}>
      <div style={{ width: "48px", height: "48px", background: "#f5f5f5", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", color: "#ccc" }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" /><rect x="9" y="3" width="6" height="4" rx="1" /></svg>
      </div>
      <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.base, fontWeight: typography.weight.semibold, color: "#444", margin: 0 }}>{t.typeUse.emptyTitle}</p>
      <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#bbb", margin: 0, maxWidth: "300px" }}>{t.typeUse.emptySubtitle}</p>
    </div>
  );
}

function LoadingState() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "64px 24px" }}>
      <span style={{ width: "24px", height: "24px", border: `3px solid ${colors.primary}`, borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
    </div>
  );
}

const cellStyle: React.CSSProperties = { padding: "14px 16px", verticalAlign: "middle" };

function RowBtn({ label, onClick, danger = false }: { label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button
      onClick={onClick}
      style={{
        fontFamily: typography.fontFamily,
        fontSize: "12px",
        fontWeight: typography.weight.semibold,
        color: danger ? colors.error : "#666",
        background: "transparent",
        border: `1px solid ${danger ? "rgba(239,68,68,0.3)" : "#e8e8e8"}`,
        borderRadius: "7px",
        padding: "6px 12px",
        cursor: "pointer",
        transition: `all ${transitions.fast}`,
        whiteSpace: "nowrap",
      }}
      onMouseEnter={e => { const el = e.currentTarget as HTMLButtonElement; el.style.borderColor = danger ? colors.error : "#111"; el.style.color = danger ? colors.error : "#111"; }}
      onMouseLeave={e => { const el = e.currentTarget as HTMLButtonElement; el.style.borderColor = danger ? "rgba(239,68,68,0.3)" : "#e8e8e8"; el.style.color = danger ? colors.error : "#666"; }}
    >
      {label}
    </button>
  );
}
